/**
 * Doctor Data Service
 * Handles doctor list processing, filtering, and lookup operations
 */

import _ from 'lodash';

/**
 * Process a single doctor list object and populate arrays/objects
 * @param {Object} doctorListObject - Doctor list to process
 * @param {Object} dentistAbbrObj - Object to populate with doctorId -> abbr mappings
 * @param {Object} dentistList - Object to populate with doctorId -> doctor data
 * @param {Array} dentistOptions - Array to populate with dropdown options
 * @param {Array} dentistArray - Array to populate with all dentists
 */
const processSingleDoctorList = (doctorListObject, dentistAbbrObj, dentistList, dentistOptions, dentistArray) => {
  if (!doctorListObject) return;
  
  Object.entries(doctorListObject).forEach(([abbr, drItem]) => {
    dentistAbbrObj[drItem.doctorId] = drItem.abbr ? drItem.abbr.toLowerCase() : null;
    dentistList[drItem.doctorId] = drItem;
    
    if ((drItem.firstName || drItem.lastName) && drItem.role !== 'Admin') {
      dentistOptions.push({
        key: drItem.doctorId,
        value: drItem.doctorId,
        text: `${drItem.firstName} ${drItem.lastName}(${abbr || ""})`,
        title: drItem.lastName,
        className: 'primary'
      });
    }
    
    if (drItem.abbr) {
      drItem.abbr = drItem.abbr.toLowerCase();
    }
    dentistArray.push(drItem);
  });
};

/**
 * Build comprehensive dentist lists from user data
 * @param {Object} user - User object containing drList and scheduleDoctors
 * @param {Array} dentistTypes - Array of dentist types to process
 * @returns {Object} Object containing dentistOptions, dentistAbbrObj, dentistList, allDentistListArray, dentistArray
 */
export const buildDentistLists = (user, dentistTypes) => {
  let dentistAbbrObj = {};
  let dentistList = {};
  let dentistOptions = [];
  let dentistArray = [];
  
  // Process all dentists from scheduleDoctors
  const allDentistListArray = Object.values(user?.scheduleDoctors ?? {}).map(drItem => {
    drItem.abbr = drItem.abbr?.toLowerCase();
    return drItem;
  });
  
  // Process doctor lists by type
  if (user?.drList && dentistTypes?.length > 0) {
    dentistTypes.forEach(dentistType => {
      processSingleDoctorList(
        user.drList[dentistType],
        dentistAbbrObj,
        dentistList,
        dentistOptions,
        dentistArray
      );
    });
  }
  
  // Sort options by last name and add "All" option at the beginning
  dentistOptions = _.sortBy(dentistOptions, ['title']);
  dentistOptions.unshift({ key: false, value: false, text: 'All' });
  
  return {
    dentistOptions,
    dentistAbbrObj,
    dentistList,
    allDentistListArray,
    dentistArray
  };
};

/**
 * Get specific list from built dentist lists
 * @param {Object} user - User object
 * @param {Array} dentistTypes - Array of dentist types
 * @param {string} listName - Name of list to return (dentistOptions, dentistAbbrObj, dentistList, allDentistListArray, dentistArray)
 * @returns {*} Requested list
 */
export const getDentistList = (user, dentistTypes, listName = 'dentistArray') => {
  const lists = buildDentistLists(user, dentistTypes);
  return lists[listName] || [];
};

/**
 * Find doctor by abbreviation in doctor list
 * @param {Array} doctorList - Array of doctor objects
 * @param {string} abbr - Doctor abbreviation to search for
 * @returns {Object|null} Doctor object or null if not found
 */
export const findDoctorByAbbr = (doctorList, abbr) => {
  if (!abbr || !Array.isArray(doctorList)) return null;
  
  const normalizedAbbr = abbr.toLowerCase();
  return doctorList.find(doctor => doctor.abbr?.toLowerCase() === normalizedAbbr) || null;
};

/**
 * Get doctor details by abbreviation from user data
 * @param {Object} user - User object
 * @param {Array} dentistTypes - Array of dentist types
 * @param {string} abbr - Doctor abbreviation
 * @returns {Object|null} Doctor details or null
 */
export const getDoctorDetailsByAbbr = (user, dentistTypes, abbr) => {
  if (!abbr) return null;
  
  // If not admin, return user's own doctor list
  if (!user?.isAdmin) {
    return user?.UdaDrList || null;
  }
  
  // For admin, search in dentist array
  const dentistArray = getDentistList(user, dentistTypes, 'dentistArray');
  return findDoctorByAbbr(dentistArray, abbr);
};

/**
 * Get doctor's full name
 * @param {Object} doctor - Doctor object
 * @returns {string} Full name or empty string
 */
export const getDoctorFullName = (doctor) => {
  if (!doctor) return '';
  
  const firstName = doctor.firstName || '';
  const lastName = doctor.lastName || '';
  
  return `Dr. ${firstName} ${lastName}`.trim();
};

/**
 * Get doctor ID from doctor object or user data
 * @param {Object} user - User object
 * @param {string} abbr - Doctor abbreviation
 * @param {Array} dentistTypes - Array of dentist types
 * @returns {number|null} Doctor ID or null
 */
export const getDoctorId = (user, abbr, dentistTypes) => {
  if (user?.isAdmin) {
    const doctor = getDoctorDetailsByAbbr(user, dentistTypes, abbr);
    return doctor?.doctorId || null;
  }
  
  return user?.Doctor?.id || null;
};
